import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Copy, Check } from 'lucide-react';

interface JsonViewerModalProps {
  /** The value to display. null = closed. */
  value: unknown;
  /** Optional column/key name to show in the header. */
  title?: string;
  onClose: () => void;
}

/**
 * Read-only viewer for object/array cell values. Shown when the user
 * double-clicks a cell rendered by `JsonChipRenderer`. We keep this
 * minimal: pretty-printed JSON inside a scrollable `<pre>`, with copy
 * and click-outside-to-close. A tree-view + edit mode are follow-ups.
 */
export function JsonViewerModal({ value, title, onClose }: JsonViewerModalProps) {
  const [copied, setCopied] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Stringify once — params.value can be expensive to serialize for
  // huge documents. Caller is responsible for not jamming megabytes here.
  const serialized = (() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch (err) {
      return `// Could not stringify value: ${err instanceof Error ? err.message : 'unknown'}\n${String(value)}`;
    }
  })();

  // Escape closes; click outside the dialog closes too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(serialized);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard denied — silently fail */ }
  }, [serialized]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="w-full max-w-[720px] mx-3 max-h-[85vh] bg-dbgo-modal border rounded-lg shadow-2xl flex flex-col">
        <div className="px-4 py-3 border-b border-subtle flex items-center justify-between flex-shrink-0">
          <div className="text-sm font-semibold text-text-primary truncate">
            {title ?? 'Value'}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 text-xs text-text-secondary hover:text-text-primary hover:bg-dbgo-tab-hover rounded-sm transition-colors"
              title="Copy as JSON"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover transition-colors"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>
        <pre className="flex-1 min-h-0 overflow-auto p-4 text-xs font-mono text-text-primary whitespace-pre">
          {serialized}
        </pre>
      </div>
    </div>
  );
}
